import {
  ShoppingCartIcon,
  UserIcon,
  ChevronDownIcon,
  MenuIcon,
  XIcon,
} from "@heroicons/react/outline";
import logo from "../../assets/logo.png";
import { useCartStore } from "../../store/cartStore";
import { useUserStore } from "../../store/userStore";
import CartDrawer from "./cartDrawer";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useState, useRef, useEffect } from "react";

const LANGUAGES = [
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "es", label: "Español", flag: "🇪🇸" },
  { code: "ro", label: "Română", flag: "🇷🇴" },
];

export default function Navbar() {
  const toggleDrawer = useCartStore((s) => s.toggleDrawer);
  const totalItems = useCartStore((s) => s.getTotalItems());
  const { isAuthenticated, user } = useUserStore();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const currentLang =
    LANGUAGES.find((l) => i18n.language?.startsWith(l.code)) || LANGUAGES[0];
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);

  // close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleUserClick = () => {
    if (isAuthenticated) {
      navigate("/tickets-store/user");
    } else {
      navigate("/tickets-store/auth");
    }
  };

  return (
    <>
      <div className="fixed top-0 left-0 w-full bg-black backdrop-blur-md z-20 flex items-center justify-between px-6 py-2">
        {/* LEFT — LOGO */}
        <button onClick={() => navigate("/tickets-store/")}>
          <img
            src={logo}
            alt="Afrodise Logo"
            className="h-6 md:h-8 object-contain hover:opacity-80 transition"
          />
        </button>

        {/* RIGHT — ICONS */}
        <div className="flex items-center gap-4">

          {/* BUY TICKETS LINK */}
          <button
            onClick={() => navigate("/tickets-store/")}
            className="text-xs tracking-widest uppercase text-gray-400 hover:text-green-400 transition hidden md:block"
          >
            {t("nav.buyTickets")}
          </button>

          {/* FAQ LINK */}
          <button
            onClick={() => navigate("/tickets-store/faq")}
            className="text-xs tracking-widest uppercase text-gray-400 hover:text-green-400 transition hidden md:block mr-5"
          >
            {t("nav.faq")}
          </button>

          {/* LANGUAGE DROPDOWN — visible on all screens */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen((v) => !v)}
              className="flex items-center gap-1.5 text-xs tracking-widest uppercase text-gray-400 hover:text-green-400 transition"
            >
              <span>{currentLang.flag}</span>
              <span>{currentLang.code.toUpperCase()}</span>
              <ChevronDownIcon
                className={`w-3 h-3 transition-transform ${dropdownOpen ? "rotate-180" : ""}`}
              />
            </button>

            {dropdownOpen && (
              <div className="absolute right-0 top-8 bg-[#02160e] border border-green-900/40 rounded-xl overflow-hidden shadow-xl z-50 min-w-[140px]">
                {LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      i18n.changeLanguage(lang.code);
                      setDropdownOpen(false);
                    }}
                    className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-xs tracking-widest uppercase transition
                      ${
                        i18n.language?.startsWith(lang.code)
                          ? "text-green-400 bg-green-900/20"
                          : "text-gray-400 hover:text-white hover:bg-green-900/10"
                      }`}
                  >
                    <span>{lang.flag}</span>
                    <span>{lang.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* CART */}
          <button
            onClick={toggleDrawer}
            className="relative text-white hover:text-green-400 transition"
          >
            <ShoppingCartIcon className="w-5 h-5" />
            {totalItems > 0 && (
              <span className="absolute -top-2 -right-2 w-4 h-4 bg-green-500 text-black text-[10px] font-bold rounded-full flex items-center justify-center">
                {totalItems > 9 ? "9+" : totalItems}
              </span>
            )}
          </button>

          {/* USER */}
          <button
            onClick={handleUserClick}
            className="flex items-center gap-2 text-white hover:text-green-400 transition"
          >
            <UserIcon className="w-5 h-5" />
            {isAuthenticated && user?.first_name && (
              <span className="text-xs text-green-400 hidden md:block">
                {user.first_name}
              </span>
            )}
          </button>

          {/* HAMBURGER — mobile only */}
          <button
            onClick={() => setMobileMenuOpen((v) => !v)}
            className="text-gray-400 hover:text-white transition md:hidden"
          >
            {mobileMenuOpen ? (
              <XIcon className="w-5 h-5" />
            ) : (
              <MenuIcon className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>

      {/* MOBILE MENU */}
      {mobileMenuOpen && (
        <div className="fixed top-12 left-0 w-full bg-black/95 backdrop-blur-md z-20 flex flex-col px-6 py-4 gap-4 md:hidden border-t border-green-900/30">
          <button
            onClick={() => {
              navigate("/tickets-store/");
              setMobileMenuOpen(false);
            }}
            className="text-xs tracking-widest uppercase text-gray-400 hover:text-green-400 transition text-left"
          >
            {t("nav.buyTickets")}
          </button>
          <button
            onClick={() => {
              navigate("/tickets-store/faq");
              setMobileMenuOpen(false);
            }}
            className="text-xs tracking-widest uppercase text-gray-400 hover:text-green-400 transition text-left"
          >
            {t("nav.faq")}
          </button>
        </div>
      )}

      {/* CART DRAWER */}
      <CartDrawer />
    </>
  );
}
